/**
 * The environment the dashboard is currently working in.
 *
 * Held outside Redux as well as in it, because the axios interceptor in
 * `api/api.js` runs outside React and has to stamp every dashboard request
 * without the caller passing anything.
 *
 * Production is represented by the ABSENCE of the parameter — that is what the
 * API treats as the default environment, and it is what every integration
 * built before environments existed sends. So nothing is attached while the
 * user is in production.
 */
export const PRODUCTION_SLUG = 'production';

const storageKey = (workspaceId) => `cms.environment.${workspaceId}`;

/** `{ workspaceId, slug, id, name }` or null. */
let active = null;

const listeners = new Set();

export const getActiveEnvironment = () => active;

export const subscribeToEnvironment = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

const notify = () => listeners.forEach((listener) => listener(active));

export const setActiveEnvironment = (workspaceId, environment) => {
    if (!workspaceId) return;

    const isProduction = !environment || environment.isDefault || environment.slug === PRODUCTION_SLUG;
    active = isProduction
        ? { workspaceId, slug: PRODUCTION_SLUG, id: environment?._id ?? null, name: environment?.name ?? 'Production' }
        : { workspaceId, slug: environment.slug, id: environment._id, name: environment.name };

    try {
        if (isProduction) {
            localStorage.removeItem(storageKey(workspaceId));
        } else {
            localStorage.setItem(storageKey(workspaceId), JSON.stringify(active));
        }
    } catch {
        // Private-mode browsers reject writes; the selection just won't persist.
    }

    notify();
};

/**
 * Restore the selection for a workspace. Called whenever the dashboard enters
 * a workspace, so a selection made in one workspace can never leak into
 * another one's requests.
 */
export const restoreActiveEnvironment = (workspaceId) => {
    if (!workspaceId) {
        active = null;
        notify();
        return null;
    }

    let stored = null;
    try {
        const raw = localStorage.getItem(storageKey(workspaceId));
        stored = raw ? JSON.parse(raw) : null;
    } catch {
        stored = null;
    }

    active =
        stored && stored.slug && stored.slug !== PRODUCTION_SLUG
            ? { ...stored, workspaceId }
            : { workspaceId, slug: PRODUCTION_SLUG, id: stored?.id ?? null, name: 'Production' };

    notify();
    return active;
};

export const isProductionSelected = () => !active || active.slug === PRODUCTION_SLUG;

/**
 * Stamp an axios request with the active environment.
 *
 * Only dashboard traffic for the workspace the selection belongs to is
 * stamped: a request aimed at another workspace would be rejected by the API,
 * since environment slugs are scoped to a workspace.
 */
export const applyEnvironmentToRequest = (config) => {
    // A caller that named an environment explicitly (the transfer screen reads
    // two at once) always wins over the ambient selection.
    if (config?.params?.environment) return config;
    if (isProductionSelected()) return config;

    const url = config?.url ?? '';
    const bodyWorkspaceId =
        config?.data && typeof config.data === 'object' ? config.data.workspaceId : null;

    if (!url.includes(active.workspaceId) && bodyWorkspaceId !== active.workspaceId) {
        return config;
    }

    config.params = { ...(config.params ?? {}), environment: active.slug };
    return config;
};

/**
 * Per-workspace view state that only makes sense inside one environment.
 *
 * `AllContents` remembers the selected content-model filter (and the search
 * term) in sessionStorage keyed by workspace alone. Content models are
 * per-environment and have their own ids, so carrying that filter across a
 * switch points it at a model that does not exist in the new environment and
 * the entry list comes back empty. View mode and sort order are not
 * environment-specific, so they are left alone.
 */
export const clearEnvironmentScopedViewState = (workspaceId) => {
    if (!workspaceId) return;
    try {
        sessionStorage.removeItem(`selectedFilterdValue_${workspaceId}`);
        sessionStorage.removeItem(`contentSearchTerm_${workspaceId}`);
    } catch {
        // Private-mode browsers reject storage access; nothing to clear.
    }
};
